<template>
    <div :class="['dynamic-field', { 'is-error': error, 'is-mandatory': metadata.isMandatory }]">
        <div class="field-label" v-if="showLabel">{{ metadata.label }}</div>
        <slot v-if="overrideControl"></slot>
        <organisation-select v-if="isOrganisationSelect" v-model="fieldValue" />
        <el-select v-else-if="isDropdown" v-model="fieldValue" filterable :no-data-text="$t('no_data')" :no-match-text="$t('no_match_found')" :placeholder="$t('select')">
            <el-option v-for="option in metadata.options"
                       :key="option.value"
                       :label="option.label"
                       :value="toInternalValue(option.value)" />
        </el-select>
        <el-date-picker v-else-if="isDate" type="date" :format="dateFormat" v-model="fieldValue" :clearable="false" :picker-options="pickerOptions"/>
        <el-date-picker v-else-if="isDateTime" type="datetime" :format="dateTimeFormat" v-model="fieldValue" :clearable="false" :picker-options="pickerOptions" />
        <el-input v-else v-model="fieldValue" :maxlength="metadata.max" :minlength="metadata.min" />
        <div class="error-message">{{ error }}</div>
    </div>
</template>

<script lang="ts">
    import Vue, { PropType } from 'vue';

    import organisationSelect from './organisation-select.vue';

    import EventBus from '@/utilities/event-bus';
    import { DisplayType, FieldMetadata, MetadataProvider, TokenType } from '@/utilities/fields/metadata';
    import { Context } from '@/utilities/contex';

    export default Vue.extend({
        name: 'dynamic-field',
        components: {
            organisationSelect,
        },
        props: {
            value: [String, Number, Array, Boolean],
            metadata: Object as PropType<FieldMetadata>,
            showLabel: {
                type: Boolean,
                default: true,
            },
        },
        data() {
            return {
                fieldValue: null as any,
                error: null as string | null,
            }
        },
        computed: {
            overrideControl(): boolean {
                return this.metadata.type == DisplayType.CPL;
            },
            isOrganisationSelect(): boolean {
                return !!this.metadata.friendlyName.match(/.+OrganisationId/);
            },
            isDropdown(): boolean {
                return [DisplayType.D, DisplayType.DI, DisplayType.SD, DisplayType.SDI, DisplayType.UI].includes(this.metadata.type);
            },
            isDate(): boolean {
                return [DisplayType.DA, DisplayType.DAT].includes(this.metadata.type);
            },
            isDateTime(): boolean {
                return [DisplayType.DT, DisplayType.DTGMT].includes(this.metadata.type);
            },
            dateFormat(): string {
                return Context.settings.dateFormat;
            },
            timeFormat(): string {
                return Context.settings.timeFormat.replace('tt', 'A');
            },
            dateTimeFormat(): string {
                return `${this.dateFormat} ${this.timeFormat}`;
            },
            pickerOptions() {
                return {
                    firstDayOfWeek: 1,
                    disabledDate(date: Date) {
                        return date < new Date(1900, 1, 1)
                    }
                }
            },
        },
        watch: {
            fieldValue() {
                const value = this.toExternalValue(this.fieldValue);
                if (value != this.value) {
                    this.$emit('input', value);
                    if (this.error) {
                        this.error = MetadataProvider.validateField(this.metadata, value);
                    }
                }
            },
            value: {
                immediate: true,
                handler() {
                    const fieldValue = this.toInternalValue(this.value);
                    if (fieldValue != this.fieldValue) {
                        this.fieldValue = fieldValue;
                    }
                }
            },
        },
        created() {
            EventBus.$on('field-invalid', this.setError);
        },
        destroyed() {
            EventBus.$off('field-invalid', this.setError);
        },
        methods: {
            toInternalValue(value: any) {
                const defaultValue = [DisplayType.D, DisplayType.DI, DisplayType.SD, DisplayType.SDI].includes(this.metadata.type)
                    && this.metadata.options.length > 0 ? this.metadata.options[0].value : '';

                switch (this.metadata.type) {
                    case DisplayType.C:
                        return !['false', 'no', '0', ''].includes((value || '').toString().toLowerCase());
                    case DisplayType.DA:
                    case DisplayType.DT:
                    case DisplayType.DTGMT:
                    case DisplayType.DAT:
                        return value ? new Date(value) : null;
                    case DisplayType.DI:
                    case DisplayType.I:
                    case DisplayType.IC:
                    case DisplayType.IS:
                    case DisplayType.SDI:
                    case DisplayType.UI:
                        return parseInt(value || defaultValue) || 0;
                    case DisplayType.R:
                    case DisplayType.REV:
                        return parseFloat(value || defaultValue) || 0.0;
                    default:
                        return (value || defaultValue).toString();
                }
            },
            toExternalValue(value: any) {
                switch (this.metadata.tokenType) {
                    case TokenType.Integer:
                        return parseInt(value) || 0;
                    case TokenType.Float:
                        return parseFloat(value) || 0.0;
                    case TokenType.Boolean:
                        return !!value;
                    default:
                        switch (this.metadata.type) {
                            case DisplayType.C:
                                return value ? 'Yes' : 'No';
                            case DisplayType.DA:
                            case DisplayType.DAT:
                                return new Date(value).toISOString().substring(0, 10);
                            case DisplayType.DT:
                            case DisplayType.DTGMT:
                                return new Date(value).toISOString();
                            default:
                                return (value || '').toString();
                        }
                }
            },
            setError(field: FieldMetadata, error: string) {
                if (field.dbName == this.metadata.dbName) {
                    this.error = error;
                }
            },
        }
    });

</script>


<style scoped lang="scss">

    .dynamic-field {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: start;
        gap: 6px;
        width: 100%;
        min-width: 0;

        .field-label {
            @extend %label-regular;
            @extend %no-overflow;
        }

        &.is-mandatory .field-label::after {
            content: ' *';
            color: var(--danger-500);
        }

        & > * {
            width: 100%;
        }

        .error-message {
            @extend %label-regular;
            @extend %no-overflow;
            color: var(--danger-500);
            height: 28px;
            margin: 0;
            opacity: 0;
            transition: all .25s;
            position: absolute;
            bottom: 0;
            background-color: var(--danger-100);
            padding: 6px 8px;
            border-radius: 6px;
            transform: translateY(50%);
            z-index: -1;
        }

        /deep/ {
            .el-input, .el-input_inner  {
                width: 100%;
            }
        }

        &.is-error {
            /deep/ .el-input__inner,
            /deep/ .el-textarea__inner {
                border-color: var(--danger-500);
            }

            &:focus-within .error-message {
                z-index: 1;
                opacity: 1;
                transform: translateY(100%);
            }
        }
    }

</style>